import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";


const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function UserChangePassword() {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [newPasswordValid, setNewPasswordValid] = useState(false);
  const [newPasswordsMatch, setNewPasswordsMatch] = useState(false);

  useEffect(() => {
    const isFourDigit = /^\d{4}$/.test(form.newPassword);
    const match = form.newPassword === form.confirmNewPassword;
    setNewPasswordValid(isFourDigit);
    setNewPasswordsMatch(match);
  }, [form.newPassword, form.confirmNewPassword]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setMessage("");
  };

  const handleChangePassword = async () => {
    if (!newPasswordValid || !newPasswordsMatch) {
      setMessage(t("user_change_password.validation.conditions"));
      setMessageVariant("error");
      return;
    }

    try {
      const payload = {
        current_password: form.currentPassword,
        new_password: form.newPassword,
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/user/change-password`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      setMessage(t("user_change_password.messages.success"));
      setMessageVariant("success");

      setForm({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });
    } catch (err) {
      setMessage(
        `${t("user_change_password.messages.failure")} ${err.response?.data?.detail || ""}`.trim()
      );
      setMessageVariant("error");
    }
  };

  return (
    <StandardScreen
      topNav={<TopNav title={t("user_change_password.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("user_change_password.title")}>
              <Stack gap="3">

                <FormField label={t("user_change_password.fields.current_password.label")}>
                  <TextInput
                    type="password"
                    name="currentPassword"
                    inputMode="numeric"
                    value={form.currentPassword}
                    onChange={handleChange}
                    placeholder={t("user_change_password.fields.current_password.placeholder")}
                  />
                </FormField>

                <FormField label={t("user_change_password.fields.new_password.label")}>
                  <TextInput
                    type="password"
                    name="newPassword"
                    inputMode="numeric"
                    value={form.newPassword}
                    onChange={handleChange}
                    placeholder={t("user_change_password.fields.new_password.placeholder")}
                  />
                </FormField>

                {!newPasswordValid && form.newPassword.length > 0 && (
                  <StatusMessage
                    variant="error"
                    message={t("user_change_password.validation.invalid_new_password")}
                  />
                )}

                <FormField label={t("user_change_password.fields.confirm_new_password.label")}>
                  <TextInput
                    type="password"
                    name="confirmNewPassword"
                    inputMode="numeric"
                    value={form.confirmNewPassword}
                    onChange={handleChange}
                    placeholder={t("user_change_password.fields.confirm_new_password.placeholder")}
                  />
                </FormField>

                {form.confirmNewPassword.length > 0 && !newPasswordsMatch && (
                  <StatusMessage
                    variant="error"
                    message={t("user_change_password.validation.password_mismatch")}
                  />
                )}

                <Button
                  onClick={handleChangePassword}
                  disabled={
                    !newPasswordValid ||
                    !newPasswordsMatch ||
                    form.currentPassword === ""
                  }
                  fullWidth
                >
                  {t("user_change_password.actions.submit")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />

              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}